import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PlanSelectionSection1() {
    const navigate = useNavigate();
    const showStripePricing = process.env.REACT_APP_ENABLE_STRIPE_PRICING_TABLE === 'true';
    
    // State management
    const [selectedPlan, setSelectedPlan] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Handle plan selection
    const handlePlanSelect = (plan) => {
        setSelectedPlan(plan);
        setError('');
    };

    // Handle form submission
    const handleContinue = async (e) => {
        e.preventDefault();
        setError('');
        
        // Validation
        if (!selectedPlan) {
            setError('Please select a plan to continue.');
            return;
        }

        setLoading(true);
        
        try {
            // Store plan selection in localStorage for checkout
            const planData = {
                plan: selectedPlan,
            };
            localStorage.setItem('selectedPlan', JSON.stringify(planData));
            
            // Navigate to checkout with plan info
            navigate('/checkout', { state: planData });
        } catch (error) {
            console.error('Navigation error:', error);
            setError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <section className="relative py-20 min-h-screen">
                <img className="hidden lg:block absolute top-0 left-0 mt-16 z-10" src="zeus-assets/icons/dots/blue-dot-left-bars.svg" alt />
                <div className="absolute top-0 left-0 bottom-0 w-full bg-gray-50" />
                <div className="relative container px-4 mx-auto">
                    <div className="flex flex-col items-center justify-center">
                        <div className="w-full max-w-lg px-4 mb-12 text-center">
                            <h2 className="mb-10 text-4xl font-semibold font-heading">Choose Your Plan</h2>
                            <p className="text-xl text-gray-500">Select the plan that best fits your needs and get started with Ordio today.</p>
                            {showStripePricing && (
                                <div className="mt-6">
                                    <a 
                                        href="/stripe-pricing" 
                                        className="text-red-400 hover:underline text-sm"
                                    >
                                        Try our simplified pricing table →
                                    </a>
                                </div>
                            )}
                        </div>
                        <div className="w-full max-w-4xl px-4">
                            <form onSubmit={handleContinue}>
                                {error && (
                                    <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded max-w-md mx-auto">
                                        {error}
                                    </div>
                                )}
                                
                                <div className="grid md:grid-cols-2 gap-6 mb-8">
                                    {/* Individual Plan */}
                                    <div 
                                        className={`p-6 lg:p-8 bg-white text-center border rounded-xl cursor-pointer transition duration-200 ${
                                            selectedPlan === 'individual' 
                                                ? 'border-red-400 ring-2 ring-red-400 ring-opacity-50' 
                                                : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                        onClick={() => handlePlanSelect('individual')}
                                    >
                                        <div className="mb-6">
                                            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                                                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-2xl font-semibold font-heading mb-2">Individual</h3>
                                            <p className="text-gray-500 mb-4">Perfect for personal use and individual productivity</p>
                                            <div className="text-3xl font-bold text-gray-900 mb-2">$15<span className="text-lg text-gray-500 font-normal">/month</span></div>
                                        </div>
                                        <ul className="text-left space-y-3 mb-6">
                                            <li className="flex items-center text-gray-600">
                                                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Unlimited AI-powered organization
                                            </li>
                                            <li className="flex items-center text-gray-600">
                                                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                10GB cloud storage
                                            </li>
                                            <li className="flex items-center text-gray-600">
                                                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Priority support
                                            </li>
                                            <li className="flex items-center text-gray-600">
                                                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Advanced analytics
                                            </li>
                                        </ul>
                                        <div className={`w-full py-3 px-4 text-sm font-medium rounded transition duration-200 ${
                                            selectedPlan === 'individual' 
                                                ? 'bg-red-400 text-white' 
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                            {selectedPlan === 'individual' ? 'Selected' : 'Select Plan'}
                                        </div>
                                    </div>

                                    {/* Family Plan */}
                                    <div 
                                        className={`p-6 lg:p-8 bg-white text-center border rounded-xl cursor-pointer transition duration-200 ${
                                            selectedPlan === 'family' 
                                                ? 'border-red-400 ring-2 ring-red-400 ring-opacity-50' 
                                                : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                        onClick={() => handlePlanSelect('family')}
                                    >
                                        <div className="mb-6">
                                            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                                                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-4-4h-1M7 20H2v-2a4 4 0 014-4h1m10-4a4 4 0 11-8 0 4 4 0 018 0zM7 10a4 4 0 118 0" />
                                                </svg>
                                            </div>
                                            <h3 className="text-2xl font-semibold font-heading mb-2">Family</h3>
                                            <p className="text-gray-500 mb-4">Up to 5 members under one subscription</p>
                                            <div className="text-3xl font-bold text-gray-900 mb-2">$40<span className="text-lg text-gray-500 font-normal">/month</span></div>
                                        </div>
                                        <ul className="text-left space-y-3 mb-6">
                                            <li className="flex items-center text-gray-600">
                                                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Includes 5 seats
                                            </li>
                                            <li className="flex items-center text-gray-600">
                                                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                All Individual features
                                            </li>
                                            <li className="flex items-center text-gray-600">
                                                <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Share with family members
                                            </li>
                                        </ul>
                                        <div className={`w-full py-3 px-4 text-sm font-medium rounded transition duration-200 ${
                                            selectedPlan === 'family' 
                                                ? 'bg-red-400 text-white' 
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                            {selectedPlan === 'family' ? 'Selected' : 'Select Plan'}
                                        </div>
                                    </div>
                                </div>

                                <div className="max-w-md mx-auto mb-6 text-xs text-gray-500 text-center">
                                    Usage beyond 500 GB will be metered at $0.02/GB.
                                </div>

                                

                                <div className="max-w-md mx-auto">
                                    <button 
                                        type="submit" 
                                        disabled={loading || !selectedPlan}
                                        className={`w-full inline-block py-4 mb-4 text-sm text-white font-medium leading-normal rounded transition duration-200 text-center ${
                                            loading || !selectedPlan
                                                ? 'bg-gray-400 cursor-not-allowed' 
                                                : 'bg-red-400 hover:bg-red-300'
                                        }`}
                                    >
                                        {loading ? 'Processing...' : 'Continue to Checkout'}
                                    </button>
                                    <div className="text-center">
                                        <span className="block text-sm text-gray-500">
                                            Need help? <a href="/contact" className="text-red-400 hover:underline">Contact Support</a>
                                        </span>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </React.Fragment>
    );
}
